<template>
    <pc-interact
        ref="sliderInteract"
        v-bind:class="wrapperClassObj"
        class="h-100 w-100 panel-wrapper"
        is-draggable
        v-on:drag-start="dragStart"
        v-on:drag-move="dragMove"
        v-on:drag-end="dragEnd">
        <div
            v-bind:style="firstStyleObj"
            v-bind:class="firstClassObj">
            <pc-viewport
                ref="firstView"
                no-interact
                expand-to-parent-size>
                <slot name="first" />
            </pc-viewport>
        </div>
        <div
            v-bind:class="secondClassObj"
            v-bind:style="secondStyleObj">
            <pc-viewport
                ref="secondView"
                no-interact
                expand-to-parent-size>
                <slot name="second" />
            </pc-viewport>
        </div>
        <div
            ref="panelSlider"
            v-bind:class="sliderClassObj"
            v-bind:style="sliderStyleObj" />
    </pc-interact>
</template>
<script>
    import { responsiveManager } from "Responsive/manager";
    function eventIsInsideRect(rect, dragEvent) {
        const x = dragEvent.clientX;
        const y = dragEvent.clientY;
        const xIn = x >= rect.left && x <= rect.left + rect.width;
        const yIn = y >= rect.top && y <= rect.top + rect.height;
        return xIn && yIn;
    }
    export default {
        props: {
            startingPanelSize: { type: Number, required: false, default: 300 },
            minPanelSize: { type: Number, required: false, default: 100 },
            maxPanelSize: { type: Number, required: false, default: 500 },
            useVerticalLayout: { type: Boolean, required: false, default: false },

            // Disable the core functionality of the panel slider so that it can be
            // used as a layout component but conditionally disabled, for instance in
            // a small screen
            noResize: { type: Boolean, required: false, default: false }
        },
        emits: ["panels-initialized", "panel-resize"],
        data() {
            return {
                secondPanelSize: this.startingPanelSize,
                dragging: false
            };
        },
        computed: {
            // Keep track of width of second panel as a ratio of global screen width
            // in case client wants to keep track of it
            panelSizeRatio() {
                return this.secondPanelSize / responsiveManager.globalWidth.value;
            },
            wrapperClassObj() {
                return {
                    "d-flex": !this.noResize,
                    "flex-column": this.useVerticalLayout
                };
            },
            firstClassObj() {
                return {
                    "flex-grow-1": true,
                    "overflow-auto": !this.noResize,
                    "border-primary-200": !this.noResize
                };
            },
            firstStyleObj() {
                if (this.noResize) {
                    return {};
                }
                if (this.useVerticalLayout) {
                    return {
                        height: `${this.secondPanelSize}px`,
                        "border-bottom": "solid 3px",
                        "flex-basis": "0"
                    };
                }
                return {
                    width: `${this.secondPanelSize}px`,
                    "border-right": "solid 3px",
                    "flex-basis": "0"
                };
            },
            secondClassObj() {
                if (this.noResize) {
                    return {};
                }
                return {
                    "overflow-auto": true,
                    "border-primary-200": true
                };
            },
            secondStyleObj() {
                if (this.noResize) {
                    return {};
                }
                if (this.useVerticalLayout) {
                    return {
                        height: `${this.secondPanelSize}px`,
                        "border-top": "solid 3px"
                    };
                }
                return {
                    width: `${this.secondPanelSize}px`,
                    "border-left": "solid 3px"
                };
            },
            sliderStyleObj() {
                if (this.noResize) {
                    return {};
                }
                if (this.useVerticalLayout) {
                    return {
                        bottom: `calc(${this.secondPanelSize}px - 3px)`
                    };
                }
                return {
                    right: `calc(${this.secondPanelSize}px - 3px)`
                };
            },
            sliderClassObj() {
                if (this.noResize) {
                    return {};
                }
                return {
                    "panel-slider": true,
                    "panel-slider-horizontal": !this.useVerticalLayout,
                    "panel-slider-vertical": this.useVerticalLayout
                };
            }
        },
        watch: {
            // Starting panel size generally shouldn't change much, but it
            // may be recalculated based on additional data after the slider
            // is first rendered, and in that case, we should take a change
            // into account
            startingPanelSize() {
                this._setBoundedSecondPanelSize(this.startingPanelSize);
            },
            noResize() {
                this.updateViews();
            }
        },
        async created() {
            await this.updateViews();
            this.$emit("panels-initialized");
        },
        methods: {
            dragStart(dragEvent) {
                const rect = this.$refs.panelSlider.getBoundingClientRect();
                if (eventIsInsideRect(rect, dragEvent)) {
                    this.dragging = true;
                    this.dragStartSecondPanelSize = this.secondPanelSize;
                }
            },
            async dragMove(dragEvent) {
                if (this.dragging) {
                    let delta = 0;
                    if (this.useVerticalLayout) {
                        delta = dragEvent.clientY0 - dragEvent.clientY;
                    } else {
                        delta = dragEvent.clientX0 - dragEvent.clientX;
                    }
                    this._setBoundedSecondPanelSize(this.dragStartSecondPanelSize + delta);
                    await this.updateViews();
                }
            },
            _setBoundedSecondPanelSize(targetSize) {
                this.secondPanelSize = Math.min(Math.max(this.minPanelSize, targetSize), this.maxPanelSize);
            },
            dragEnd() {
                this.dragging = false;
                this.$emit("panel-resize", this.panelSizeRatio);
            },
            async updateViews() {
                await this.$nextTick();
                this.$refs.firstView.update();
                this.$refs.secondView.update();
            }
        }
    };
</script>
<style lang="scss" scoped>
    .panel-wrapper {
        position: relative;
    }

    .panel-slider {
        position: absolute;
        background-color: var(--bs-primary);
        opacity: 0;
        transition: opacity 0.5s;
        &:hover {
            opacity: 0.5;
        }
    }
    .panel-slider-horizontal {
        width: 6px;
        height: 100%;
        cursor: col-resize;
    }
    .panel-slider-vertical {
        width: 100%;
        height: 6px;
        cursor: row-resize;
    }
</style>
